import { useState } from "react";
import { Download } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { authenticatedRequest } from "@/lib/graphql/client";
import { ADMIN_EXPORT_USER_WORKSPACES } from "@/lib/graphql/queries/user-export";

interface WorkspaceSnapshotBundle {
  formatVersion: number;
  exportedAt: string;
  source: { userId: string };
  workspaces: unknown[];
}

interface ExportResponse {
  adminExportUserWorkspaces: WorkspaceSnapshotBundle;
}

function downloadJson(bundle: WorkspaceSnapshotBundle, userId: string): void {
  const blob = new Blob([JSON.stringify(bundle, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  const date = new Date().toISOString().split("T")[0];
  link.href = url;
  link.download = `novela-user-${userId}-${date}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default function UserDataExportPage() {
  const [userId, setUserId] = useState("");
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    const trimmedUserId = userId.trim();
    if (!trimmedUserId) {
      toast.error("유저 ID를 입력해주세요.");
      return;
    }

    setIsExporting(true);
    try {
      const data = await authenticatedRequest<ExportResponse>(
        ADMIN_EXPORT_USER_WORKSPACES,
        { userId: trimmedUserId }
      );
      const bundle = data.adminExportUserWorkspaces;
      downloadJson(bundle, trimmedUserId);
      toast.success(`워크스페이스 ${bundle.workspaces.length}개 export 완료`);
    } catch (error) {
      toast.error("export 실패", {
        description:
          error instanceof Error ? error.message : "다시 시도해주세요.",
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          유저 데이터 export
        </h1>
        <p className="text-muted-foreground leading-normal">
          특정 유저의 모든 워크스페이스(파일·캐릭터·플롯)를 JSON 스냅샷으로
          내보냅니다. 로컬 클라이언트에서 불러와 버그 재현에 사용합니다.
        </p>
      </div>

      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle className="text-base">스냅샷 내보내기</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="export-user-id">유저 ID</Label>
            <Input
              id="export-user-id"
              value={userId}
              onChange={(event) => setUserId(event.target.value)}
              placeholder="users 컬렉션의 _id"
              onKeyDown={(event) => {
                if (event.key === "Enter") handleExport();
              }}
            />
          </div>
          <Button onClick={handleExport} disabled={isExporting}>
            <Download className="mr-2 h-4 w-4" />
            {isExporting ? "내보내는 중..." : "JSON 다운로드"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
